import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectIsOrgOwnerOrAdmin } from "../authentication/authenticationSlice";
import { ContractStatus } from "../contracts/models/ContractStatus";
import { ContractType } from "../contracts/models/ContractType";
import { fetchContracts } from "../contracts/slice-extensions/contracts-slice-extension";
import { ClosedPeriodCause } from "../closed-periods/models/ClosedPeriodCause";
import { ClosedPeriodScope } from "../closed-periods/models/ClosedPeriodScope";
import { ClosedPeriodTreatment } from "../closed-periods/models/ClosedPeriodTreatment";
import { createClosedPeriod } from "../closed-periods/slice-extensions/closed-periods-slice-extension";

export interface ClosedPeriodBridgeOffer {
  memberId: string;
  memberName: string;
  startDate: string;
  endDate: string;
  note: string;
}

/**
 * The absence → billing bridge: after an admin records an absence for a member
 * on an active subscription contract, offer one click to also create the
 * matching per-member Closed Period (agreed-off, not charged) so the monthly
 * draft follows reality. Display-only members and pack contracts get no offer.
 */
export const useClosedPeriodBridge = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const isOrgOwnerOrAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);
  const [offer, setOffer] = useState<ClosedPeriodBridgeOffer | null>(null);
  const [busy, setBusy] = useState(false);

  const maybeOffer = useCallback(
    async (candidate: ClosedPeriodBridgeOffer) => {
      if (!isOrgOwnerOrAdmin) return;
      const result = await dispatch(
        fetchContracts({ memberId: candidate.memberId, status: ContractStatus.Active, pageSize: 50 }),
      );
      if (!fetchContracts.fulfilled.match(result)) return;
      const hasSubscription = result.payload.items.some(
        (contract) =>
          contract.type === ContractType.FlexibleSubscription ||
          contract.type === ContractType.FlatSubscription,
      );
      if (hasSubscription) {
        setOffer(candidate);
      }
    },
    [dispatch, isOrgOwnerOrAdmin],
  );

  const confirm = useCallback(async () => {
    if (!offer) return;
    setBusy(true);
    const result = await dispatch(
      createClosedPeriod({
        scope: ClosedPeriodScope.PerMember,
        cause: ClosedPeriodCause.AgreedOff,
        treatment: ClosedPeriodTreatment.NotCharged,
        startDate: offer.startDate,
        endDate: offer.endDate,
        memberId: offer.memberId,
        notes: offer.note || undefined,
      }),
    );
    setBusy(false);
    if (createClosedPeriod.fulfilled.match(result)) {
      toast.success(t("absences.bridge.toastCreated", "Closed period created — the subscription skips this time."));
      setOffer(null);
    } else {
      toast.error(result.payload?.message ?? t("absences.bridge.createFailed", "Failed to create the closed period."));
    }
  }, [dispatch, offer, t]);

  const dismiss = useCallback(() => setOffer(null), []);

  return { offer, busy, maybeOffer, confirm, dismiss };
};
